import { Form } from 'antd';
import { useLoading } from 'react-simplikit';

import { useModalContext } from '@/components/Modal/Modal';
import { MainText } from '@/components/Typography';
import Typo from '@/components/Typography/Typo';
import { getApiErrorMessage } from '@/error-handler/api-error-messages/ApiErrorMessages';
import { ApiError } from '@/error-handler/ApiError';
import service from '@/services';
import { popup } from '@/stores/pure/PopupStore';
import { toast } from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SCHEDULE_PERIOD, WEEKDAY } from '@/types/commonConst';
import { getFeedbackSourceName, type SOURCE_TYPE } from '@/types/feedbackConst';
import { getTimezoneDayjs } from '@/utils/time';

import type { CreateScrapingJobRequest } from '../api/dto';
import { CreateScrapingJobForm } from '../components/CreateScrapingJobForm/CreateScrapingJobForm';
import { DuplicatedJobTable } from '../components/DuplicatedJobTable/DuplicatedJobTable';
import {
  getIsItemPerDayField,
  getMaxItemCountByScraper,
  getScraperFieldList,
} from '../configs/config-actions';
import type { SCRAPER_REQ_FIELD_KEY } from '../configs/scraper-field-map/field-const';
import { useCreateProducts } from '../hooks/useCreateProduct';
import { useInitialSource } from '../hooks/useInitalSource';
import {
  type DraftScrapingJob,
  type ScraperFieldInput,
  type SCRAPING_JOB_STATUS,
  SCRAPING_JOB_TYPE,
  SCRAPING_SORT,
} from '../types';
import { validateMaxItems } from '../utils/validateMaxItemsValue';

type Props = {
  initialSourceType?: SOURCE_TYPE;
  jobType: SCRAPING_JOB_TYPE;
  onSuccess: (type: SCRAPING_JOB_TYPE) => void;
};

const CreateScrapingJobModal = (props: Props) => {
  const { createProduct } = useCreateProducts();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const { closeModal } = useModalContext();
  const [loading, startLoading] = useLoading();

  const [form] = Form.useForm<DraftScrapingJob>();

  const {
    currentSourceType,
    scraperOptions,
    currentScraperOption,
    sourceOptions,
  } = useInitialSource(form);

  const scraperFieldList = currentScraperOption
    ? getScraperFieldList(currentScraperOption)
    : [];

  const confirmLogic = async (
    values: DraftScrapingJob,
    scraperInputs: ScraperFieldInput,
  ): Promise<{ status: SCRAPING_JOB_STATUS; type: SCRAPING_JOB_TYPE }> => {
    const reqBody: CreateScrapingJobRequest = {
      jobs: [
        {
          title: values.title || getFeedbackSourceName(values.source_type),
          type: props.jobType,
          // weekday 이외의 값은 기본 설정으로 전달
          schedule:
            props.jobType === SCRAPING_JOB_TYPE.SCHEDULED
              ? values.schedule
              : undefined,
          start_date:
            props.jobType === SCRAPING_JOB_TYPE.ONETIME
              ? values.start_date
              : undefined,
          end_date:
            props.jobType === SCRAPING_JOB_TYPE.ONETIME
              ? values.end_date
                ? getTimezoneDayjs(values.end_date)
                    .hour(23)
                    .minute(59)
                    .second(59)
                    .millisecond(0)
                    .toISOString()
                : undefined
              : undefined,
          source_type: values.source_type,
          scraper_name: values.scraper_name,
          scraper_input: scraperInputs,
          shift_days: values.shift_days ? Number(values.shift_days) : undefined,
        },
      ],
      need_confirm: values.need_confirm,
    };

    const errors = await service.scraping.validateScrapingJob(reqBody);
    if (errors.length > 0) {
      popup({
        title: 'Validation failed',
        message: (
          <div>
            {errors[0].field_errors.map((error) => {
              return (
                <Typo
                  key={error.cause}
                  type="body3"
                  className="text-destructive"
                >
                  [{error.related_fields.join(', ')}] : {error.cause}
                </Typo>
              );
            })}
          </div>
        ),
        type: 'alert',
        popupTheme: 'negative',
      });

      throw new Error('Validation failed');
    }

    try {
      const res = await service.scraping.createScrapingJob(reqBody);
      // Scraping Job 생성 성공 시 Product 생성
      await createProduct({
        product_brand_name: values.brand,
        product_line_name: values.product_line,
        product_name: values.product_name,
      });

      closeModal();
      const result = res[0];

      if (result) {
        return result;
      } else {
        throw new Error('CreateScrapingJob Result is not exist');
      }
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.data.client_code === 'scraping_job__exist_in_progress_job') {
          toast(getApiErrorMessage(error.data.client_code), 'fail');
          throw error;
        }
      }
      throw error;
    }
  };

  const onFinish = async (values: DraftScrapingJob) => {
    if (!currWorkspace) return;
    if (loading) return;

    const isItemPerDay = getIsItemPerDayField(values.scraper_name);
    const defaultMaxItems = getMaxItemCountByScraper(values.scraper_name);

    try {
      // 사용자가 직접 입력하는 필드들
      const scraperInputs: Partial<ScraperFieldInput> = {
        brand: values.brand,
        product_line: values.product_line,
        product_name: values.product_name,
        page_url: values.page_url,
        group_alias: values.group_alias,
        username: values.username,
        cafe_name: values.cafe_name,
        exact_match: values.exact_match,
        sort: values.sort,
        link: values.link,
        links: values.links,
        keyword: values.keyword,
        keywords: values.keywords,
        definition: values.definition,
        cafe_id: values.cafe_id,
        subreddit: values.subreddit,
        include_keywords: values.include_keywords,
        exclude_keywords: values.exclude_keywords,
        all_options: values.all_options,
      };

      // 스크래퍼 별 유효한 필드만 남기고 삭제
      Object.keys(scraperInputs).forEach((key) => {
        if (!scraperFieldList.includes(key as SCRAPER_REQ_FIELD_KEY)) {
          delete scraperInputs[key as keyof Partial<ScraperFieldInput>];
        }
      });

      // 공통 필드 및 시스템 자동 입력 필드
      // 스크래퍼 타입에 따라 적절한 필드 사용
      const params: ScraperFieldInput = {
        ...scraperInputs,
        language: currWorkspace.language,
        ...(isItemPerDay
          ? {
              item_per_day: validateMaxItems(
                values.item_per_day,
                defaultMaxItems,
              ),
            }
          : { max_items: validateMaxItems(values.max_items, defaultMaxItems) }),
      };

      const exists = await service.scraping.checkExistsScrapingJob({
        jobs: [
          {
            scraper_name: values.scraper_name,
            scraper_input: params,
          },
        ],
      });

      if (exists.length > 0) {
        popup({
          title: 'Duplicate job detected.',
          style: {
            width: 600,
          },
          message: (
            <div className="flex flex-col gap-5 flex-1 overflow-auto">
              <MainText dim>
                You already have a feedback collection running for this platform
                and target. Do you want to continue anyway?
              </MainText>
              <DuplicatedJobTable
                data={exists.flat()}
                onExternalLinkClick={(jobId) => {
                  const url = `${window.location.origin}/workspace/${currWorkspace?.alias}/settings/integration/scraping/${jobId}`;
                  window.open(url, '_blank');
                }}
              />
            </div>
          ),
          type: 'confirm',
          popupTheme: 'negative',
          confirmText: 'Create anyway',
          onConfirm: async () => {
            try {
              const result = await startLoading(confirmLogic(values, params));
              props.onSuccess(result.type);
            } catch {
              // 에러는 confirmLogic 내부에서 이미 처리됨 (popup/toast)
              // throw하지 않아야 DefaultPopup의 loading 상태가 해제됨
            }
          },
          cancelText: 'Cancel',
        });
        return;
      }

      const result = await startLoading(confirmLogic(values, params));
      props.onSuccess(result.type);
    } catch (error) {
      console.debug(error);
      toast('Failed to create request', 'fail');
    }
  };

  return (
    <CreateScrapingJobForm
      form={form}
      jobType={props.jobType}
      sourceOptions={sourceOptions}
      scraperOptions={scraperOptions}
      currentScraperOption={currentScraperOption}
      loading={loading}
      onSubmit={onFinish}
      onClose={closeModal}
      initialValues={{
        title: '',
        source_type: props.initialSourceType || currentSourceType,
        scraper_name: currentScraperOption,
        sort: SCRAPING_SORT.RECENT,
        schedule: {
          period: SCHEDULE_PERIOD.WEEKLY,
          hour: 0,
          weekday: WEEKDAY.SUNDAY,
        },
        shift_days: undefined,
      }}
    />
  );
};

export default CreateScrapingJobModal;
